// src/components/CallShieldAlert.tsx
// ──────────────────────────────────────────────────────────────
// Incoming P2P call notification — designed as a confident
// summons, not an emergency alert. Three progressive states:
//   1. Ready (default) — calm, reassuring
//   2. Waiting (~25s) — gentle emphasis
//   3. Critical (60s+) — urgency styling
// Mount at app root level, outside <Routes>.
// ──────────────────────────────────────────────────────────────

import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCallShield } from "../hooks/useCallShield";

// ── Types ────────────────────────────────────────────────────

type AlertPhase = "ready" | "waiting" | "critical";

// ── Icons ────────────────────────────────────────────────────

function ShieldCheckIcon({ size = 28, color = "#fff" }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function PhoneIcon({ size = 18, color = "#fff" }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

// ── Helpers ──────────────────────────────────────────────────

function formatPhone(num: string): string {
  if (!num) return "";
  const cleaned = num.replace(/\D/g, "");
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return num;
}

function getPhase(holdSeconds: number): AlertPhase {
  if (holdSeconds >= 60) return "critical";
  if (holdSeconds >= 25) return "waiting";
  return "ready";
}

// ── Phase-driven config (purple palette) ─────────────────────

const PHASE_CONFIG = {
  ready: {
    title: "Reviewer ready",
    subtitle: "Connected and holding for you",
    accentColor: "#14b8a6",
    accentBorder: "rgba(20,184,166,.12)",
    dotColor: "#10b981",
    barColor: "linear-gradient(90deg, transparent, rgba(20,184,166,.35), transparent)",
    backdrop: "rgba(0, 0, 0, 0.8)",
    shieldColor: "#a78bfa",
    shieldBg: "rgba(167, 139, 250, 0.15)",
    shieldRing: "rgba(167, 139, 250, 0.3)",
  },
  waiting: {
    title: "Reviewer ready",
    subtitle: "Waiting — please join now",
    accentColor: "#8b5cf6",
    accentBorder: "rgba(139,92,246,.15)",
    dotColor: "#8b5cf6",
    barColor: "linear-gradient(90deg, transparent, rgba(139,92,246,.5), transparent)",
    backdrop: "rgba(0, 0, 0, 0.85)",
    shieldColor: "#a78bfa",
    shieldBg: "rgba(167, 139, 250, 0.15)",
    shieldRing: "rgba(167, 139, 250, 0.3)",
  },
  critical: {
    title: "Still holding",
    subtitle: "Join now to avoid reschedule",
    accentColor: "#a78bfa",
    accentBorder: "rgba(167,139,250,.18)",
    dotColor: "#a78bfa",
    barColor: "linear-gradient(90deg, #8b5cf6, #a78bfa, #8b5cf6)",
    backdrop: "rgba(0, 0, 0, 0.9)",
    shieldColor: "#c4b5fd",
    shieldBg: "rgba(196, 181, 253, 0.15)",
    shieldRing: "rgba(196, 181, 253, 0.3)",
  },
};

// ── Component ────────────────────────────────────────────────

export function CallShieldAlert() {
  const navigate = useNavigate();
  const {
    hasIncomingCall,
    incomingCall,
    caseInfo,
    holdDuration,
    isJoining,
    joinError,
    joinCall,
    dismiss,
  } = useCallShield();

  const phase = getPhase(holdDuration);
  const config = PHASE_CONFIG[phase];

  // Prevent body scroll when alert is showing
  useEffect(() => {
    if (hasIncomingCall) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [hasIncomingCall]);

  // Build case display lines
  const diagnosisLine = useMemo(() => {
    return caseInfo?.diagnoses || null;
  }, [caseInfo]);

  const procedureLine = useMemo(() => {
    return caseInfo?.request_details || caseInfo?.request_type || null;
  }, [caseInfo]);

  if (!hasIncomingCall) return null;

  const handleJoin = async () => {
    await joinCall();
    if (incomingCall?.case_id) {
      navigate(`/case/${incomingCall.case_id}?callMode=true`);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: config.backdrop,
        backdropFilter: "blur(12px)",
        animation: "cs-fadein 0.25s ease-out",
        transition: "background 0.6s ease",
      }}
    >
      <style>{`
        @keyframes cs-fadein {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes cs-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.15); }
        }
        @keyframes cs-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes cs-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div
        style={{
          width: "100%",
          maxWidth: 380,
          margin: "0 16px",
          borderRadius: 20,
          overflow: "hidden",
          background: "#0f172a",
          border: `1px solid ${config.accentBorder}`,
          boxShadow: `0 0 0 1px rgba(167, 139, 250, 0.2), 0 0 60px rgba(167, 139, 250, 0.1), 0 25px 50px rgba(0, 0, 0, 0.5)`,
          transition: "border-color 0.6s ease",
        }}
      >
        {/* ── Top accent bar ─────────────────────────────── */}
        <div
          style={{
            height: 4,
            background: config.barColor,
            transition: "background 0.6s ease",
          }}
        />

        {/* ── Header with shield icon ────────────────────── */}
        <div style={{ padding: "24px 24px 16px", textAlign: "center" }}>
          {/* Shield icon circle */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: config.shieldBg,
              marginBottom: 16,
              position: "relative",
              transition: "background 0.6s ease",
            }}
          >
            {/* Pulse ring */}
            <div
              style={{
                position: "absolute",
                inset: -8,
                borderRadius: "50%",
                border: `2px solid ${config.shieldRing}`,
                animation: "cs-pulse 2s ease-in-out infinite",
                transition: "border-color 0.6s ease",
              }}
            />
            <ShieldCheckIcon size={28} color={config.shieldColor} />
          </div>

          {/* Title + subtitle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              marginBottom: 4,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: config.dotColor,
                animation: "cs-dot 2s ease-in-out infinite",
                flexShrink: 0,
                transition: "background 0.6s ease",
              }}
            />
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: config.accentColor,
                transition: "color 0.6s ease",
              }}
            >
              {config.title}
            </span>
          </div>
          <div
            style={{
              fontSize: 14,
              color: "rgba(255, 255, 255, 0.5)",
            }}
          >
            {config.subtitle}
          </div>
        </div>

        {/* ── Case context card ──────────────────────────── */}
        <div
          style={{
            margin: "0 16px",
            padding: "14px 16px",
            borderRadius: 12,
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
          }}
        >
          {/* Insurer badge */}
          {caseInfo?.insurance_company && (
            <div
              style={{
                display: "inline-block",
                padding: "3px 10px",
                borderRadius: 6,
                background: "rgba(20, 184, 166, 0.15)",
                color: "#2dd4bf",
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 10,
              }}
            >
              {caseInfo.insurance_company}
            </div>
          )}

          {/* Diagnosis */}
          {diagnosisLine && (
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "#f1f5f9",
                lineHeight: 1.4,
                marginBottom: procedureLine ? 6 : 0,
              }}
            >
              {diagnosisLine}
            </div>
          )}

          {/* Requested service */}
          {procedureLine && (
            <div style={{ fontSize: 13, color: "rgba(255, 255, 255, 0.6)" }}>
              {procedureLine}
            </div>
          )}

          {/* Caller number */}
          {incomingCall?.caller_number && (
            <div
              style={{
                marginTop: 10,
                paddingTop: 8,
                borderTop: "1px solid rgba(255, 255, 255, 0.08)",
                fontSize: 12,
                color: "rgba(255, 255, 255, 0.2)",
              }}
            >
              {formatPhone(incomingCall.caller_number)}
            </div>
          )}
        </div>

        {/* ── Error ──────────────────────────────────────── */}
        {joinError && (
          <div
            style={{
              margin: "12px 16px 0",
              padding: "8px 12px",
              borderRadius: 8,
              background: "rgba(167, 139, 250, 0.08)",
              border: "1px solid rgba(167, 139, 250, 0.15)",
              fontSize: 12,
              color: "#c4b5fd",
              textAlign: "center",
            }}
          >
            {joinError}
          </div>
        )}

        {/* ── Join Call ──────────────────────────────────── */}
        <div style={{ padding: "20px 16px 24px" }}>
          <button
            onClick={handleJoin}
            disabled={isJoining}
            style={{
              width: "100%",
              padding: "16px 24px",
              borderRadius: 14,
              border: "none",
              background: isJoining
                ? "rgba(16, 185, 129, 0.3)"
                : "linear-gradient(135deg, #10b981, #059669)",
              color: "#fff",
              fontSize: 17,
              fontWeight: 700,
              cursor: isJoining ? "wait" : "pointer",
              transition: "all 0.15s ease",
              boxShadow: isJoining
                ? "none"
                : "0 4px 20px rgba(16, 185, 129, 0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
            }}
          >
            {isJoining ? (
              <>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    border: "2px solid rgba(255,255,255,0.3)",
                    borderTop: "2px solid #fff",
                    borderRadius: "50%",
                    animation: "cs-spin 0.8s linear infinite",
                    display: "inline-block",
                  }}
                />
                Connecting...
              </>
            ) : (
              <>
                <PhoneIcon size={18} color="#fff" />
                Join Call
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}